import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { LogIn, UserPlus, KeyRound, X } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError, formatAxiosError, setCurrentLang } from '@/lib/api';
import { createT } from '@/lib/i18n';

export default function LoginPage() {
  const { user, login, register, forgotPassword } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const lang = searchParams.get('lang') || 'ru';
  const t = createT(lang);
  // На странице логина у нас ещё нет user.lang_code — используем URL-параметр,
  // чтобы toast-ошибки бэкенда тоже переводились на английский.
  useEffect(() => { setCurrentLang(lang); }, [lang]);
  const [form, setForm] = useState({
    loginEmail: '', loginPassword: '',
    regEmail: '', regPassword: '', regCompany: '', regName: '', regCountry: '',
    regCode: '', regDirection: 'logistics'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  // Сообщаем о просроченной сессии (событие из axios-интерцептора).
  useEffect(() => {
    const handler = () => toast.error(lang === 'en'
      ? 'Session expired. Please sign in again.'
      : 'Сессия истекла. Войдите заново.');
    window.addEventListener('bq:session-expired', handler);
    return () => window.removeEventListener('bq:session-expired', handler);
  }, [lang]);

  if (user) { navigate('/dashboard', { replace: true }); return null; }

  const update = (key) => (e) => setForm(p => ({ ...p, [key]: e.target.value }));

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await login(form.loginEmail, form.loginPassword);
      toast.success(t('auth.login.success'));
      navigate('/dashboard');
    } catch (err) {
      setError(formatAxiosError(err));
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await register({
        email: form.regEmail, password: form.regPassword,
        company_name: form.regCompany, contact_name: form.regName,
        country: form.regCountry, secret_code: form.regCode,
        tender_direction: form.regDirection,
      });
      toast.success(t('auth.register.success'));
      navigate('/dashboard');
    } catch (err) {
      setError(formatAxiosError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left: Form */}
      <div className="w-full lg:w-[480px] xl:w-[520px] flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-3 mb-8">
            <h1 className="font-heading text-2xl font-black tracking-tighter text-slate-900">BaseQuote</h1>
          </div>
          <p className="text-sm text-slate-500 mb-6">{t('app.subtitle')}</p>

          {error && (
            <div className="bg-rose-50 text-rose-700 border border-rose-200 px-3 py-2 rounded-sm mb-4 text-sm" data-testid="auth-error">
              {error}
            </div>
          )}

          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6 bg-slate-100">
              <TabsTrigger value="login" data-testid="tab-login">{t('auth.login')}</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">{t('auth.register')}</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-slate-700">{t('auth.email')}</Label>
                  <Input type="email" value={form.loginEmail} onChange={update('loginEmail')}
                    placeholder="email@company.com" required data-testid="login-email"
                    className="border-slate-300 focus:border-[#0046AD] focus:ring-1 focus:ring-[#0046AD] rounded-sm" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-slate-700">{t('auth.password')}</Label>
                  <Input type="password" value={form.loginPassword} onChange={update('loginPassword')}
                    placeholder={t('auth.password.placeholder')} required data-testid="login-password"
                    className="border-slate-300 focus:border-[#0046AD] focus:ring-1 focus:ring-[#0046AD] rounded-sm" />
                </div>
                <Button type="submit" disabled={loading} data-testid="login-submit"
                  className="w-full bg-[#0046AD] hover:bg-[#003A8C] rounded-sm font-semibold">
                  <LogIn className="w-4 h-4 mr-2" /> {loading ? t('auth.login.loading') : t('auth.login.btn')}
                </Button>
                <div className="text-center pt-1">
                  <button type="button" onClick={() => { setForgotEmail(form.loginEmail || ''); setForgotOpen(true); }}
                    data-testid="forgot-password-link"
                    className="text-xs text-slate-500 hover:text-[#0046AD] underline underline-offset-2">
                    {lang === 'en' ? 'Forgot password?' : 'Забыли пароль?'}
                  </button>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-slate-700">{t('auth.email')}</Label>
                  <Input type="email" value={form.regEmail} onChange={update('regEmail')} required data-testid="register-email"
                    className="border-slate-300 rounded-sm" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-slate-700">{t('auth.password')}</Label>
                  <Input type="password" value={form.regPassword} onChange={update('regPassword')} required data-testid="register-password"
                    className="border-slate-300 rounded-sm" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-slate-700">{t('auth.company')}</Label>
                    <Input value={form.regCompany} onChange={update('regCompany')} required data-testid="register-company"
                      className="border-slate-300 rounded-sm" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-slate-700">{t('auth.name')}</Label>
                    <Input value={form.regName} onChange={update('regName')} required data-testid="register-name"
                      className="border-slate-300 rounded-sm" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-slate-700">{t('auth.country')}</Label>
                    <Input value={form.regCountry} onChange={update('regCountry')} data-testid="register-country"
                      className="border-slate-300 rounded-sm" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-slate-700">{t('auth.secret_code')}</Label>
                    <Input value={form.regCode} onChange={update('regCode')} required data-testid="register-secret-code"
                      placeholder={t('auth.secret_code.placeholder')} className="border-slate-300 rounded-sm" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-slate-700">Направление тендеров</Label>
                  <select value={form.regDirection} onChange={update('regDirection')} required
                    data-testid="register-direction"
                    className="flex h-9 w-full rounded-sm border border-slate-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0046AD]/30">
                    <option value="logistics">Логистика</option>
                    <option value="purchase">Закупка</option>
                    <option value="purchase_oil">Закупка масла</option>
                    <option value="all">Все</option>
                  </select>
                  <p className="text-[11px] text-slate-500">
                    «Логистика» или «Закупка» — только лоты своего направления. «Все» — доступ к лотам всех направлений (требует отдельный код доступа).
                  </p>
                </div>
                <Button type="submit" disabled={loading} data-testid="register-submit"
                  className="w-full bg-[#0046AD] hover:bg-[#003A8C] rounded-sm font-semibold">
                  <UserPlus className="w-4 h-4 mr-2" /> {loading ? t('auth.register.loading') : t('auth.register.btn')}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Right: Background */}
      <div className="hidden lg:block flex-1 relative bg-gradient-to-br from-[#0046AD] via-[#003585] to-[#001f4d]">
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-12">
          <h2 className="font-heading text-4xl font-black text-white tracking-tighter mb-3">BaseQuote</h2>
          <p className="text-white/70 text-lg max-w-md leading-relaxed">
            {t('auth.hero.text')}
          </p>
        </div>
      </div>

      {/* Forgot-password modal */}
      {forgotOpen && (
        <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4"
          onClick={() => setForgotOpen(false)} data-testid="forgot-password-modal">
          <div className="bg-white rounded-sm shadow-2xl w-full max-w-md p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
                <KeyRound className="w-4 h-4 text-[#0046AD]" />
                {lang === 'en' ? 'Reset Password' : 'Восстановление пароля'}
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setForgotOpen(false)}
                data-testid="forgot-password-close" className="rounded-sm">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">
              {lang === 'en'
                ? 'Enter your email and we will send you a link to set a new password.'
                : 'Укажите email — мы вышлем ссылку для установки нового пароля. Ссылка действует 30 минут.'}
            </p>
            <form onSubmit={async (e) => {
              e.preventDefault();
              setForgotLoading(true);
              try {
                const res = await forgotPassword(forgotEmail);
                toast.success(res?.message || (lang === 'en'
                  ? 'If the email is registered, a link has been sent.'
                  : 'Если email зарегистрирован, на него отправлена ссылка.'));
                setForgotOpen(false);
              } catch (err) {
                toast.error(formatAxiosError(err));
              } finally { setForgotLoading(false); }
            }} className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-slate-700">Email</Label>
                <Input type="email" required value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="email@company.com" data-testid="forgot-password-email"
                  className="border-slate-300 rounded-sm" />
              </div>
              <Button type="submit" disabled={forgotLoading}
                data-testid="forgot-password-submit"
                className="w-full bg-[#0046AD] hover:bg-[#003A8C] rounded-sm font-semibold">
                {forgotLoading
                  ? (lang === 'en' ? 'Sending...' : 'Отправка...')
                  : (lang === 'en' ? 'Send reset link' : 'Выслать ссылку')}
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
